import { useTheme } from '@emotion/react'
import { Autocomplete, Stack, TextField, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

interface ProcessStatusOption {
    id: string
    statusLabel: string
    groupBy: string
}

interface ProcessStatusFilterProps {
    processStatus: string[]
    processStatusOptions: ProcessStatusOption[]
    onChange: (processStatus: string[]) => void
}

export function ProcessStatusFilter({
    processStatus,
    processStatusOptions,
    onChange,
}: ProcessStatusFilterProps) {
    const theme = useTheme()
    const [selectedGroups, setSelectedGroups] = useState<string[]>([])

    // Get selected process status objects for Autocomplete
    // processStatus array contains statusLABELS (e.g., "Active", "Pending"), not IDs
    const selectedProcessStatuses = useMemo(
        () =>
            processStatusOptions.filter((status) =>
                processStatus.includes(status.statusLabel)
            ),
        [processStatusOptions, processStatus]
    )

    // Update selectedGroups based on current selections
    useEffect(() => {
        const groupedValues = Object.groupBy(
            selectedProcessStatuses,
            ({ groupBy }) => groupBy
        )
        const newSelectedGroups: string[] = []

        for (const group in groupedValues) {
            const fullOptionsInGroup = processStatusOptions.filter(
                (option) => option.groupBy === group
            )
            if (fullOptionsInGroup.length === groupedValues[group]?.length) {
                newSelectedGroups.push(group)
            }
        }

        setSelectedGroups(newSelectedGroups)
    }, [selectedProcessStatuses, processStatusOptions])

    const handleGroupClick = (selectedGroup: string) => {
        const isGroupSelected = selectedGroups.includes(selectedGroup)

        // Toggle group selection state
        setSelectedGroups((prev) =>
            isGroupSelected
                ? prev.filter((group) => group !== selectedGroup)
                : [...prev, selectedGroup]
        )

        const newSelectedOptions = processStatusOptions
            .filter((option) => option.groupBy === selectedGroup)
            .map((option) => ({
                ...option,
            }))

        let updatedProcessStatuses: ProcessStatusOption[]

        if (isGroupSelected) {
            // Remove all options in the group
            updatedProcessStatuses = selectedProcessStatuses.filter(
                (ps) => ps.groupBy !== selectedGroup
            )
        } else {
            // Add all options in the group (excluding already selected ones)
            const existingInGroup = selectedProcessStatuses.filter(
                (ps) => ps.groupBy === selectedGroup
            )
            const newInGroup = newSelectedOptions.filter(
                (newOption) =>
                    !existingInGroup.some(
                        (existing) => existing.id === newOption.id
                    )
            )

            updatedProcessStatuses = [...selectedProcessStatuses, ...newInGroup]
        }

        handleProcessStatusChange(updatedProcessStatuses)
    }

    const handleProcessStatusChange = (
        selectedStatuses: ProcessStatusOption[] | null
    ) => {
        // Pass statusLABELS to the hook, not IDs (for human-readable URLs)
        const newStatuses = (selectedStatuses || []).map(
            (status) => status.statusLabel
        )
        onChange(newStatuses)

        // Clear selectedGroups if no items are selected
        if (newStatuses.length === 0) {
            setSelectedGroups([])
        }
    }

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            options={processStatusOptions}
            value={selectedProcessStatuses}
            onChange={(_, newValue) => handleProcessStatusChange(newValue)}
            getOptionLabel={(option) => option.statusLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            groupBy={(option) => option.groupBy}
            renderGroup={(params) => (
                <Stack key={params.group} style={{ cursor: 'pointer' }}>
                    <Stack
                        onClick={() => handleGroupClick(params.group)}
                        direction={'row'}
                        alignItems={'center'}
                        sx={{
                            backgroundColor: selectedGroups.includes(
                                params.group
                            )
                                ? theme.palette.other.primary_50
                                : 'transparent',
                            padding: '8px',
                            borderRadius: '4px',
                            boxShadow: selectedGroups.includes(params.group)
                                ? `0px 0px 0px 2px ${theme.palette.other.primary_50}`
                                : 'none',
                            '&:hover': {
                                backgroundColor: theme.palette.other.lightGrey,
                            },
                        }}
                    >
                        <Typography sx={{ fontWeight: 'bold' }}>
                            {params.group}
                        </Typography>
                    </Stack>
                    {params.children}
                </Stack>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Process Status"
                    placeholder="Select process statuses..."
                />
            )}
        />
    )
}
